import pick from 'lodash/pick';

import { types as sdkTypes } from '../../util/sdkLoader';
import analytics from '../../util/affiliate';
import { isTransactionsTransitionInvalidTransition, storableError } from '../../util/errors';
import {
  getReview1Transition,
  getReview2Transition,
  getLateReviewTransition,
  txIsInFirstReviewBy,
} from '../../util/transaction';
import { updatedEntities, denormalisedEntities } from '../../util/data';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { callGraphQLApi } from '../../util/api';

const { UUID } = sdkTypes;

const CUSTOMER = 'customer';

const IMAGE_VARIANTS = {
  'fields.image': [
    // Profile images
    'variants.square-small',
    'variants.square-small2x',

    // Listing images:
    'variants.landscape-crop',
    'variants.landscape-crop2x',
  ],
};

// ================ Action types ================ //

export const SET_INITAL_VALUES = 'app/ReviewPage/SET_INITIAL_VALUES';

export const FETCH_TRANSACTION_REQUEST = 'app/ReviewPage/FETCH_TRANSACTION_REQUEST';
export const FETCH_TRANSACTION_SUCCESS = 'app/ReviewPage/FETCH_TRANSACTION_SUCCESS';
export const FETCH_TRANSACTION_ERROR = 'app/ReviewPage/FETCH_TRANSACTION_ERROR';

export const FETCH_TRANSITIONS_REQUEST = 'app/ReviewPage/FETCH_TRANSITIONS_REQUEST';
export const FETCH_TRANSITIONS_SUCCESS = 'app/ReviewPage/FETCH_TRANSITIONS_SUCCESS';
export const FETCH_TRANSITIONS_ERROR = 'app/ReviewPage/FETCH_TRANSITIONS_ERROR';

export const SEND_REVIEW_REQUEST = 'app/ReviewPage/SEND_REVIEW_REQUEST';
export const SEND_REVIEW_SUCCESS = 'app/ReviewPage/SEND_REVIEW_SUCCESS';
export const SEND_REVIEW_ERROR = 'app/ReviewPage/SEND_REVIEW_ERROR';

export const FETCH_REVIEW_DATA_REQUEST = 'app/ReviewPage/FETCH_REVIEW_DATA_REQUEST';
export const FETCH_REVIEW_DATA_SUCCESS = 'app/ReviewPage/FETCH_REVIEW_DATA_SUCCESS';
export const FETCH_REVIEW_DATA_ERROR = 'app/ReviewPage/FETCH_REVIEW_DATA_ERROR';

// ================ Reducer ================ //
const initialState = {
  fetchTransactionInProgress: false,
  fetchTransactionError: null,
  transactionRef: null,
  sendReviewInProgress: false,
  sendReviewError: null,
  fetchTransitionsInProgress: false,
  fetchTransitionsError: null,
  processTransitions: null,
  fetchReviewDataInProgress: false,
  fetchReviewDataError: null,
  review: null,
};

const reviewPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case SET_INITAL_VALUES:
      return { ...initialState, ...payload };
    case FETCH_TRANSACTION_REQUEST:
      return {
        ...state,
        fetchTransactionInProgress: true,
        fetchTransactionError: null,
        transactionRole: payload.transactionRole,
      };
    case FETCH_TRANSACTION_SUCCESS: {
      const transactionRef = { id: payload.data.data.id, type: 'transaction' };
      return { ...state, fetchTransactionInProgress: false, transactionRef };
    }
    case FETCH_TRANSACTION_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, fetchTransactionInProgress: false, fetchTransactionError: payload };
    case FETCH_TRANSITIONS_REQUEST:
      return { ...state, fetchTransitionsInProgress: true, fetchTransitionsError: null };
    case FETCH_TRANSITIONS_SUCCESS:
      return { ...state, fetchTransitionsInProgress: false, processTransitions: payload };
    case FETCH_TRANSITIONS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, fetchTransitionsInProgress: false, fetchTransitionsError: payload };
    case SEND_REVIEW_REQUEST:
      return { ...state, sendReviewInProgress: true, sendReviewError: null };
    case SEND_REVIEW_SUCCESS:
      return { ...state, sendReviewInProgress: false };
    case SEND_REVIEW_ERROR:
      return { ...state, sendReviewInProgress: false, sendReviewError: payload };
    case FETCH_REVIEW_DATA_REQUEST:
      return { ...state, fetchReviewDataInProgress: true, sendReviewError: null };
    case FETCH_REVIEW_DATA_SUCCESS:
      return { ...state, fetchReviewDataInProgress: false, review: payload };
    case FETCH_REVIEW_DATA_ERROR:
      return { ...state, fetchReviewDataInProgress: false, sendReviewError: payload };

    default:
      return state;
  }
};

export default reviewPageReducer;

// ================ Action creators ================ //

export const setInitialValues = initialValues => ({
  type: SET_INITAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

const fetchTransactionRequest = txRole => ({
  type: FETCH_TRANSACTION_REQUEST,
  payload: { transactionRole: txRole },
});
const fetchTransactionSuccess = response => ({
  type: FETCH_TRANSACTION_SUCCESS,
  payload: response,
});
const fetchTransactionError = e => ({ type: FETCH_TRANSACTION_ERROR, error: true, payload: e });

const fetchTransitionsRequest = () => ({ type: FETCH_TRANSITIONS_REQUEST });
const fetchTransitionsSuccess = response => ({
  type: FETCH_TRANSITIONS_SUCCESS,
  payload: response,
});
const fetchTransitionsError = e => ({ type: FETCH_TRANSITIONS_ERROR, error: true, payload: e });

const sendReviewRequest = () => ({ type: SEND_REVIEW_REQUEST });
const sendReviewSuccess = payload => ({ type: SEND_REVIEW_SUCCESS, payload });
const sendReviewError = e => ({ type: SEND_REVIEW_ERROR, error: true, payload: e });

const fetchReviewDataRequest = () => ({ type: FETCH_REVIEW_DATA_REQUEST });
const fetchReviewDataSuccess = payload => ({ type: FETCH_REVIEW_DATA_SUCCESS, payload });
const fetchReviewDataError = e => ({ type: FETCH_REVIEW_DATA_ERROR, error: true, payload: e });

// ================ Thunks ================ //

const listingRelationship = txResponse => {
  return txResponse.data.data.relationships.listing.data;
};

export const fetchTransaction = (id, txRole) => (dispatch, getState, sdk) => {
  dispatch(fetchTransactionRequest(txRole));
  let txResponse = null;
  return sdk.transactions
    .show(
      {
        id,
        include: [
          'customer',
          'customer.profileImage',
          'provider',
          'provider.profileImage',
          'listing',
          'booking',
          'reviews',
          'reviews.author',
          'reviews.subject',
        ],
        ...IMAGE_VARIANTS,
      },
      { expand: true }
    )
    .then(response => {
      txResponse = response;
      const listingId = listingRelationship(response).id;
      const entities = updatedEntities({}, response.data);
      const listingRef = { id: listingId, type: 'listing' };
      const transactionRef = { id, type: 'transaction' };
      const denormalised = denormalisedEntities(entities, [listingRef, transactionRef]);
      const listing = denormalised[0];

      const canFetchListing = listing && listing.attributes && !listing.attributes.deleted;
      if (canFetchListing) {
        return sdk.listings.show({
          id: listingId,
          include: ['author', 'author.profileImage', 'images'],
          ...IMAGE_VARIANTS,
        });
      }
      return response;
    })
    .then(response => {
      dispatch(addMarketplaceEntities(txResponse));
      dispatch(addMarketplaceEntities(response));
      dispatch(fetchTransactionSuccess(txResponse));
      dispatch(fetchCurrentUser({ include: ['stripeCustomer.defaultPaymentMethod'] }));
      return response;
    })
    .catch(e => {
      dispatch(fetchTransactionError(storableError(e)));
      throw e;
    });
};

const REVIEW_TX_INCLUDES = ['reviews', 'reviews.author', 'reviews.subject'];

// If other party has already sent a review, we need to make transition to
// TRANSITION_REVIEW_2_BY_<CUSTOMER/PROVIDER>
const sendReviewAsSecond = (id, params, role, dispatch, sdk) => {
  const transition = getReview2Transition(role === CUSTOMER);

  const include = REVIEW_TX_INCLUDES;

  return sdk.transactions
    .transition({ id, transition, params }, { expand: true, include, ...IMAGE_VARIANTS })
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(sendReviewSuccess({ txId: id }));
      return response;
    })
    .catch(e => {
      dispatch(sendReviewError(storableError(e)));

      // Rethrow so the page can track whether the sending failed, and
      // keep the message in the form for a retry.
      throw e;
    });
};

// If other party has already sent a review, we need to make transition to
// TRANSITION_REVIEW_2_BY_<CUSTOMER/PROVIDER>
const sendLateReview = (id, params, role, dispatch, sdk) => {
  const transition = getLateReviewTransition(role === CUSTOMER);

  const include = REVIEW_TX_INCLUDES;

  return sdk.transactions
    .transition({ id, transition, params }, { expand: true, include, ...IMAGE_VARIANTS })
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(sendReviewSuccess({ txId: id }));
      return response;
    })
    .catch(e => {
      dispatch(sendReviewError(storableError(e)));

      // Rethrow so the page can track whether the sending failed, and
      // keep the message in the form for a retry.
      throw e;
    });
};

// If other party has not yet sent a review, we need to make transition to
// TRANSITION_REVIEW_1_BY_<CUSTOMER/PROVIDER>
// However, the other party might have made the review after previous data synch point.
// So, error is likely to happen and then we must try another state transition
// by calling sendReviewAsSecond().
const sendReviewAsFirst = (id, params, role, dispatch, sdk) => {
  const transition = getReview1Transition(role === CUSTOMER);
  const include = REVIEW_TX_INCLUDES;
  return sdk.transactions
    .transition({ id, transition, params }, { expand: true, include, ...IMAGE_VARIANTS })
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(sendReviewSuccess({ txId: id }));
      return response;
    })
    .catch(e => {
      // If transaction transition is invalid, lets try another endpoint.
      if (isTransactionsTransitionInvalidTransition(e)) {
        return sendReviewAsSecond(id, params, role, dispatch, sdk);
      }
      dispatch(sendReviewError(storableError(e)));

      // Rethrow so the page can track whether the sending failed, and
      // keep the message in the form for a retry.
      throw e;
    });
};

export const sendReview = (role, tx, reviewRating, reviewContent) => (dispatch, getState, sdk) => {
  const params = {
    reviewRating,
    reviewContent,
    protectedData: { utm: { ...analytics.all() } },
  };
  const txStateOtherPartyFirst = txIsInFirstReviewBy(tx, role !== CUSTOMER);
  const lastTransition = tx.attributes.transitions.slice(-1)[0].transition;
  dispatch(sendReviewRequest());

  if (txStateOtherPartyFirst) {
    return sendReviewAsSecond(tx.id, params, role, dispatch, sdk);
  }
  if (lastTransition === 'transition/provider-late-cancel') {
    return sendLateReview(tx.id, params, role, dispatch, sdk);
  }
  return sendReviewAsFirst(tx.id, params, role, dispatch, sdk);
};

export const fetchNextTransitions = id => (dispatch, getState, sdk) => {
  dispatch(fetchTransitionsRequest());

  return sdk.processTransitions
    .query({ transactionId: id })
    .then(res => {
      dispatch(fetchTransitionsSuccess(res.data.data));
    })
    .catch(e => {
      dispatch(fetchTransitionsError(storableError(e)));
    });
};

export const fetchReviewData = (transactionId, authorId) => async dispatch => {
  dispatch(fetchReviewDataRequest());
  try {
    const response = await callGraphQLApi(
      `
        query FetchReview($authorId: ID!, $transactionId: ID!) {
          GetReview(input: { authorId: $authorId, transactionId: $transactionId}) {
            item {
              id
            }
          }
        }`,
      {
        authorId: authorId.uuid,
        transactionId: transactionId.uuid,
      }
    );

    const review = response?.data?.GetReview?.item;

    // Dispatching action to handle successful data retrieval
    dispatch(
      fetchReviewDataSuccess({
        review,
      })
    );
    return Promise.resolve(review);
  } catch (error) {
    dispatch(fetchReviewDataError(error));
    return Promise.reject(error);
  }
};

// loadData is a collection of async calls that need to be made
// before page has all the info it needs to render itself
export const loadData = params => dispatch => {
  const txId = new UUID(params.id);
  const txRole = params.transactionRole;

  return dispatch(fetchTransaction(txId, txRole)).then(() =>
    Promise.all([dispatch(fetchNextTransitions(txId))])
  );
};
